import React, { useEffect, useMemo, useState } from "react";
import { Close } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  DEFAULT_WEBHOOK_TEMPLATE,
  TELEMETRY_BATCH_CUSTOM_FIELDS_TEMPLATE,
  TELEMETRY_BATCH_WEBHOOK_TEMPLATE,
  TELEMETRY_CUSTOM_FIELDS_TEMPLATE,
  TELEMETRY_WEBHOOK_TEMPLATE,
} from "./api";

const AUTH_TYPES = [
  { value: "hmac", label: "HMAC signature (recommended)" },
  { value: "bearer", label: "Bearer token" },
  { value: "basic", label: "Basic auth" },
  { value: "custom_headers", label: "Custom headers" },
  { value: "none", label: "None (insecure)" },
];

const PII_PROFILES = [
  { value: "minimal", label: "Minimal — no email/GPS/media URLs" },
  { value: "operational", label: "Operational — names & site address" },
  { value: "full", label: "Full — all fields (use with care)" },
];

function buildEmptyForm() {
  return {
    name: "",
    description: "",
    url: "",
    enabled: true,
    auth_type: "hmac",
    auth_config: {},
    content_type: "application/json",
    template_body: DEFAULT_WEBHOOK_TEMPLATE,
    template_headers: null,
    client_organization_id: "",
    target_scope: "organization",
    target_device_id: "",
    target_device_ids: [],
    target_device_type_id: "",
    target_device_type_ids: [],
    telemetry_delivery_mode: "real_time",
    batch_interval_seconds: 60,
    batch_max_records: 5000,
    threshold_rules: null,
    threshold_cooldown_seconds: 300,
    pii_profile: "minimal",
    filter_rules: null,
    subscribed_events: [],
    max_retries: 5,
    timeout_seconds: 30,
    rate_limit_per_minute: "",
    signing_secret: "",
  };
}

function endpointToForm(endpoint) {
  if (!endpoint) return buildEmptyForm();
  return {
    name: endpoint.name || "",
    description: endpoint.description || "",
    url: endpoint.url || "",
    enabled: endpoint.enabled !== false,
    auth_type: endpoint.auth_type || "hmac",
    auth_config: endpoint.auth_config || {},
    content_type: endpoint.content_type || "application/json",
    template_body: endpoint.template_body || DEFAULT_WEBHOOK_TEMPLATE,
    template_headers: endpoint.template_headers || null,
    client_organization_id: endpoint.client_organization_id || "",
    target_scope: endpoint.target_scope || "organization",
    target_device_id: endpoint.target_device_id || "",
    target_device_ids:
      endpoint.target_device_ids || (endpoint.target_device_id ? [endpoint.target_device_id] : []),
    target_device_type_id: endpoint.target_device_type_id || "",
    target_device_type_ids:
      endpoint.target_device_type_ids ||
      (endpoint.target_device_type_id ? [endpoint.target_device_type_id] : []),
    telemetry_delivery_mode: endpoint.telemetry_delivery_mode || "real_time",
    batch_interval_seconds: endpoint.batch_interval_seconds ?? 60,
    batch_max_records: endpoint.batch_max_records ?? 5000,
    threshold_rules: endpoint.threshold_rules || null,
    threshold_cooldown_seconds: endpoint.threshold_cooldown_seconds ?? 300,
    pii_profile: endpoint.pii_profile || "minimal",
    filter_rules: endpoint.filter_rules || null,
    subscribed_events: endpoint.subscribed_events || [],
    max_retries: endpoint.max_retries ?? 5,
    timeout_seconds: endpoint.timeout_seconds ?? 30,
    rate_limit_per_minute: endpoint.rate_limit_per_minute ?? "",
    signing_secret: "",
  };
}

export default function EndpointFormDialog({
  open,
  onClose,
  onSubmit,
  saving,
  endpoint,
  catalogEvents,
  clientOrganizations = [],
  targetDevices = [],
  targetDeviceTypes = [],
}) {
  const [form, setForm] = useState(buildEmptyForm());
  const [filterJson, setFilterJson] = useState("");
  const [headersJson, setHeadersJson] = useState("");
  const [thresholdJson, setThresholdJson] = useState("");
  const [customAuthHeadersJson, setCustomAuthHeadersJson] = useState("");
  const [bearerToken, setBearerToken] = useState("");
  const [basicUser, setBasicUser] = useState("");
  const [basicPass, setBasicPass] = useState("");
  const [domainFilter, setDomainFilter] = useState("all");
  const [error, setError] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [bearerTokenConfigured, setBearerTokenConfigured] = useState(false);
  const [basicPasswordConfigured, setBasicPasswordConfigured] = useState(false);

  const isEdit = Boolean(endpoint?.id);
  const subscribesTelemetry =
    form.subscribed_events.includes("telemetry.received");

  useEffect(() => {
    if (!open) return;
    const next = endpointToForm(endpoint);
    const authMeta = endpoint?.auth_config || {};
    setForm(next);
    setFilterJson(
      next.filter_rules ? JSON.stringify(next.filter_rules, null, 2) : "",
    );
    setHeadersJson(
      next.template_headers
        ? JSON.stringify(next.template_headers, null, 2)
        : "",
    );
    setThresholdJson(
      next.threshold_rules ? JSON.stringify(next.threshold_rules, null, 2) : "",
    );
    setCustomAuthHeadersJson(
      authMeta.headers && Object.keys(authMeta.headers).length
        ? JSON.stringify(authMeta.headers, null, 2)
        : "",
    );
    setBearerToken("");
    setBearerTokenConfigured(Boolean(authMeta.token_configured));
    setBasicUser(authMeta.username || "");
    setBasicPass("");
    setBasicPasswordConfigured(Boolean(authMeta.password_configured));
    setDomainFilter("all");
    setError("");
    setSubmitAttempted(false);
  }, [open, endpoint]);

  const domains = useMemo(() => {
    const set = new Set(
      (catalogEvents || []).map((e) => e.domain).filter(Boolean),
    );
    return ["all", ...Array.from(set).sort()];
  }, [catalogEvents]);

  const filteredCatalog = useMemo(() => {
    if (domainFilter === "all") return catalogEvents || [];
    return (catalogEvents || []).filter((e) => e.domain === domainFilter);
  }, [catalogEvents, domainFilter]);

  const selectedEventObjects = useMemo(
    () =>
      (catalogEvents || []).filter((e) =>
        form.subscribed_events.includes(e.type),
      ),
    [catalogEvents, form.subscribed_events],
  );
  const isTelemetryTemplate =
    form.template_body === TELEMETRY_WEBHOOK_TEMPLATE ||
    form.template_body === TELEMETRY_CUSTOM_FIELDS_TEMPLATE ||
    form.template_body.includes("resource.attributes['timestamp']") ||
    form.template_body.includes('resource.attributes["timestamp"]') ||
    form.template_body.includes("resource.attributes['data']") ||
    form.template_body.includes('resource.attributes["data"]');

  const selectedClientOrganization = useMemo(
    () =>
      (clientOrganizations || []).find(
        (org) => org.id === form.client_organization_id,
      ) || null,
    [clientOrganizations, form.client_organization_id],
  );
  const selectedTargetDevices = useMemo(
    () => {
      const ids = new Set(form.target_device_ids || []);
      return (targetDevices || []).filter((device) => ids.has(device.id));
    },
    [targetDevices, form.target_device_ids],
  );
  const selectedTargetDeviceTypes = useMemo(
    () => {
      const ids = new Set(form.target_device_type_ids || []);
      return (targetDeviceTypes || []).filter((deviceType) =>
        ids.has(deviceType.id),
      );
    },
    [targetDeviceTypes, form.target_device_type_ids],
  );

  const buildPayload = () => {
    let filter_rules = null;
    if (filterJson.trim()) {
      filter_rules = JSON.parse(filterJson);
    }

    let template_headers = null;
    if (headersJson.trim()) {
      template_headers = JSON.parse(headersJson);
    }
    let threshold_rules = null;
    if (thresholdJson.trim()) {
      threshold_rules = JSON.parse(thresholdJson);
    }

    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      url: form.url.trim(),
      enabled: form.enabled,
      auth_type: form.auth_type,
      content_type: form.content_type,
      template_body: form.template_body,
      template_headers,
      client_organization_id: form.client_organization_id || null,
      target_scope: subscribesTelemetry ? form.target_scope : "organization",
      target_device_id:
        subscribesTelemetry && form.target_scope === "device"
          ? (form.target_device_ids || [])[0] || null
          : null,
      target_device_ids:
        subscribesTelemetry && form.target_scope === "device"
          ? form.target_device_ids || []
          : [],
      target_device_type_id:
        subscribesTelemetry && form.target_scope === "device_type"
          ? (form.target_device_type_ids || [])[0] || null
          : null,
      target_device_type_ids:
        subscribesTelemetry && form.target_scope === "device_type"
          ? form.target_device_type_ids || []
          : [],
      telemetry_delivery_mode: subscribesTelemetry
        ? form.telemetry_delivery_mode
        : "real_time",
      batch_interval_seconds:
        subscribesTelemetry && form.telemetry_delivery_mode === "batch"
          ? Number(form.batch_interval_seconds) || 60
          : null,
      batch_max_records:
        subscribesTelemetry && form.telemetry_delivery_mode === "batch"
          ? Number(form.batch_max_records) || 5000
          : null,
      threshold_rules:
        subscribesTelemetry && form.telemetry_delivery_mode === "threshold"
          ? threshold_rules
          : null,
      threshold_cooldown_seconds:
        subscribesTelemetry && form.telemetry_delivery_mode === "threshold"
          ? Number(form.threshold_cooldown_seconds) || 300
          : null,
      pii_profile: form.pii_profile,
      filter_rules,
      telemetry_export_fields: [],
      subscribed_events: form.subscribed_events,
      max_retries: Number(form.max_retries) || 5,
      timeout_seconds: Number(form.timeout_seconds) || 30,
      rate_limit_per_minute: form.rate_limit_per_minute
        ? Number(form.rate_limit_per_minute)
        : null,
    };

    if (form.signing_secret?.trim()) {
      payload.signing_secret = form.signing_secret.trim();
    }

    if (form.auth_type === "bearer") {
      const token = bearerToken.trim();
      if (token || !isEdit) {
        payload.auth_config = { token };
      }
    } else if (form.auth_type === "basic") {
      payload.auth_config = {
        username: basicUser,
        password: basicPass,
      };
    } else if (form.auth_type === "custom_headers") {
      payload.auth_config = customAuthHeadersJson.trim()
        ? JSON.parse(customAuthHeadersJson)
        : { headers: {} };
    }

    return payload;
  };

  const handleSave = async () => {
    setError("");
    setSubmitAttempted(true);
    if (!form.name.trim()) {
      setError("Name is required.");
      return;
    }
    if (!form.url.trim()) {
      setError("HTTPS URL is required.");
      return;
    }
    if (!form.url.trim().toLowerCase().startsWith("https://")) {
      setError("Webhook URL must use HTTPS.");
      return;
    }
    if (!form.subscribed_events.length) {
      setError("Select at least one event type.");
      return;
    }
    if (
      subscribesTelemetry &&
      form.target_scope === "device" &&
      !(form.target_device_ids || []).length
    ) {
      setError("Select at least one target device.");
      return;
    }
    if (
      subscribesTelemetry &&
      form.target_scope === "device_type" &&
      !(form.target_device_type_ids || []).length
    ) {
      setError("Select at least one target device type.");
      return;
    }
    if (
      form.auth_type === "bearer" &&
      !bearerToken.trim() &&
      (!isEdit || !bearerTokenConfigured)
    ) {
      setError("Bearer token is required.");
      return;
    }
    const needsRateLimit = selectedEventObjects.some(
      (event) => event.high_volume,
    );
    if (
      needsRateLimit &&
      (!subscribesTelemetry || form.telemetry_delivery_mode !== "threshold") &&
      !form.rate_limit_per_minute
    ) {
      setError(
        "Rate limit per minute is required for high-volume events (e.g. telemetry).",
      );
      return;
    }
    if (
      subscribesTelemetry &&
      form.telemetry_delivery_mode === "threshold" &&
      !thresholdJson.trim()
    ) {
      setError("Threshold rules are required for threshold mode.");
      return;
    }
    if (
      form.rate_limit_per_minute &&
      (Number(form.rate_limit_per_minute) < 1 ||
        Number(form.rate_limit_per_minute) > 10000)
    ) {
      setError(
        "Rate limit must be between 1 and 10,000 deliveries per minute.",
      );
      return;
    }
    try {
      const payload = buildPayload();
      await onSubmit(payload);
    } catch (err) {
      setError(err?.message || "Invalid JSON in filters or headers.");
    }
  };

  const nameError = submitAttempted && !form.name.trim();
  const urlRequiredError = submitAttempted && !form.url.trim();
  const urlFormatError =
    submitAttempted &&
    Boolean(form.url.trim()) &&
    !form.url.trim().toLowerCase().startsWith("https://");
  const eventsError = submitAttempted && !form.subscribed_events.length;
  const sectionSx = {
    border: "1px solid #E5E7EB",
    borderRadius: 2,
    p: { xs: 2, md: 2.5 },
    backgroundColor: "#fff",
  };
  const gridSx = {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: "repeat(12, 1fr)" },
    gap: 2,
  };
  const sectionHeading = (title, helper) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
        {title}
      </Typography>
      {helper ? (
        <Typography variant="body2" color="text.secondary">
          {helper}
        </Typography>
      ) : null}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { width: "min(1180px, 96vw)" } }}
    >
      <DialogTitle
        sx={{
          p: 2,
          background: "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))"
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800, color: "#fff" }}>
            {isEdit ? "Edit webhook endpoint" : "Create webhook endpoint"}
          </Typography>

          <IconButton onClick={onClose} size="small" sx={{color:"#fff"}}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: "#F8FAFC", p: 3 }}>
        <Stack spacing={2.5}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {form.pii_profile === "full" ? (
            <Alert severity="warning">
              Full PII may include emails, GPS, and media URLs in webhook payloads.
            </Alert>
          ) : null}

          <Box sx={sectionSx}>
            {sectionHeading(
              "1. Destination",
              "Name the endpoint and enter the client URL that will receive webhook POST requests.",
            )}
            <Box sx={gridSx}>
              <Box sx={{ gridColumn: { xs: "1", md: "span 8" } }}>
            <TextField
              fullWidth
              label="Name"
              required
              error={nameError}
              helperText={nameError ? "Name is required." : ""}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
              </Box>
              <Box
                sx={{
                  gridColumn: { xs: "1", md: "span 4" },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "flex-start", md: "flex-end" },
                }}
              >
                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={
                    <Switch
                      checked={form.enabled}
                      onChange={(e) =>
                        setForm({ ...form, enabled: e.target.checked })
                      }
                    />
                  }
                  label="Endpoint enabled"
                />
              </Box>
              <Box sx={{ gridColumn: { xs: "1", md: "span 8" } }}>
            <TextField
              fullWidth
              label="HTTPS URL"
              required
              placeholder="https://your-server.com/webhooks/trackcore"
              error={urlRequiredError || urlFormatError}
              helperText={
                urlRequiredError
                  ? "HTTPS URL is required."
                  : urlFormatError
                    ? "Webhook URL must use HTTPS."
                    : ""
              }
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
            />
              </Box>
              <Box sx={{ gridColumn: { xs: "1", md: "span 4" } }}>
                <Autocomplete
                  options={clientOrganizations || []}
                  value={selectedClientOrganization}
                  onChange={(_, value) =>
                    setForm({
                      ...form,
                      client_organization_id: value?.id || "",
                    })
                  }
                  getOptionLabel={(option) =>
                    option?.name
                      ? `${option.name}${option.code ? ` (${option.code})` : ""}`
                      : ""
                  }
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Client organization"
                      placeholder="None"
                      helperText="Optional. Leave empty for external clients."
                    />
                  )}
                />
              </Box>
              <Box sx={{ gridColumn: "1 / -1" }}>
            <TextField
              fullWidth
              multiline
              minRows={1}
              label="Notes"
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />
              </Box>
            </Box>
          </Box>

          <Box sx={sectionSx}>
            {sectionHeading(
              "2. Events",
              "Select the event types that should call this endpoint.",
            )}
            <Box sx={gridSx}>
              <Box sx={{ gridColumn: { xs: "1", md: "span 4" } }}>
            <FormControl fullWidth>
              <InputLabel>Event domain filter</InputLabel>

              <Select
                label="Event domain filter"
                value={domainFilter}
                onChange={(e) => setDomainFilter(e.target.value)}
              >
                {domains.map((d) => (
                  <MenuItem key={d} value={d}>
                    {d === "all" ? "All domains" : d}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
              </Box>

              <Box sx={{ gridColumn: { xs: "1", md: "span 8" } }}>
            <Autocomplete
              multiple
              options={filteredCatalog}
              groupBy={(option) => option.domain}
              getOptionLabel={(option) => option.type}
              value={selectedEventObjects}
              onChange={(_, value) => {
                const nextEvents = value.map((v) => v.type);
                const nextHasTelemetry = nextEvents.includes("telemetry.received");
                setForm({
                  ...form,
                  subscribed_events: nextEvents,
                  template_body:
                    !nextHasTelemetry && isTelemetryTemplate
                      ? DEFAULT_WEBHOOK_TEMPLATE
                      : form.template_body,
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Subscribed events"
                  required
                  error={eventsError}
                  helperText={
                    eventsError ? "Select at least one event type." : ""
                  }
                  placeholder="Search events..."
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.type}
                    size="small"
                    label={option.type}
                  />
                ))
              }
              renderOption={(props, option) => (
                <li {...props} key={option.type}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {option.type}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      {option.description}
                      {option.high_volume ? " - high volume" : ""}
                    </Typography>
                  </Box>
                </li>
              )}
            />
              </Box>
            </Box>
          </Box>

          <Box sx={sectionSx}>
            {sectionHeading(
              "3. Security and limits",
              "Pick how the receiver verifies requests and set delivery protection values.",
            )}
            <Box sx={gridSx}>
              <Box sx={{ gridColumn: { xs: "1", md: "span 4" } }}>
            <FormControl fullWidth>
              <InputLabel>Authentication</InputLabel>
              <Select
                label="Authentication"
                value={form.auth_type}
                onChange={(e) =>
                  setForm({ ...form, auth_type: e.target.value })
                }
              >
                {AUTH_TYPES.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
              </Box>

              <Box sx={{ gridColumn: { xs: "1", md: "span 4" } }}>
            <FormControl fullWidth>
              <InputLabel>PII profile</InputLabel>
              <Select
                label="PII profile"
                value={form.pii_profile}
                onChange={(e) =>
                  setForm({ ...form, pii_profile: e.target.value })
                }
              >
                {PII_PROFILES.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
              </Box>

          {/* Bearer */}
          {form.auth_type === "bearer" && (
                <Box sx={{ gridColumn: { xs: "1", md: "span 4" } }}>
              <TextField
                fullWidth
                label="Bearer token"
                type="password"
                value={bearerToken}
                onChange={(e) => setBearerToken(e.target.value)}
                placeholder={
                  isEdit && bearerTokenConfigured
                    ? "Token saved — leave blank to keep, or type new token to replace"
                    : "Enter the token your client will verify"
                }
                helperText={
                  isEdit && bearerTokenConfigured && !bearerToken
                    ? "A Bearer token is already configured for this endpoint."
                    : "Sent as Authorization: Bearer <token> on each webhook POST."
                }
              />
                </Box>
          )}

          {/* Custom Headers */}
          {form.auth_type === "custom_headers" && (
                <Box sx={{ gridColumn: { xs: "1", md: "span 4" } }}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Custom auth headers (JSON)"
                placeholder='{"X-Api-Key": "your-key"}'
                value={customAuthHeadersJson}
                onChange={(e) => setCustomAuthHeadersJson(e.target.value)}
                InputProps={{
                  sx: { fontFamily: "monospace", fontSize: 13 },
                }}
              />
                </Box>
          )}

          {/* Basic Auth */}
          {form.auth_type === "basic" && (
            <>
                  <Box sx={{ gridColumn: { xs: "1", md: "span 4" } }}>
                <TextField
                  fullWidth
                  label="Username"
                  value={basicUser}
                  onChange={(e) => setBasicUser(e.target.value)}
                />
                  </Box>

                  <Box sx={{ gridColumn: { xs: "1", md: "span 4" } }}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={basicPass}
                  onChange={(e) => setBasicPass(e.target.value)}
                  placeholder={
                    isEdit && basicPasswordConfigured
                      ? "Leave blank to keep current password"
                      : ""
                    }
                  />
                  </Box>
            </>
          )}

          {/* HMAC */}
          {form.auth_type === "hmac" && (
                <Box sx={{ gridColumn: { xs: "1", md: "span 4" } }}>
              <TextField
                fullWidth
                label={
                  isEdit
                    ? "Rotate signing secret (leave blank to keep current)"
                    : "Signing secret (optional — auto-generated if empty)"
                }
                type="password"
                value={form.signing_secret}
                onChange={(e) =>
                  setForm({ ...form, signing_secret: e.target.value })
                }
              />
                </Box>
          )}

              <Box sx={{ gridColumn: { xs: "1", md: "span 4" } }}>
            <TextField
              fullWidth
              type="number"
              label="Max retries"
              value={form.max_retries}
              onChange={(e) =>
                setForm({ ...form, max_retries: e.target.value })
              }
            />
              </Box>

              <Box sx={{ gridColumn: { xs: "1", md: "span 4" } }}>
            <TextField
              fullWidth
              type="number"
              label="Timeout (seconds)"
              value={form.timeout_seconds}
              onChange={(e) =>
                setForm({ ...form, timeout_seconds: e.target.value })
              }
            />
              </Box>

              <Box sx={{ gridColumn: { xs: "1", md: "span 4" } }}>
            <TextField
              fullWidth
              type="number"
              label="Rate limit / min"
              helperText="Required for high-volume events (telemetry, deployment progress)."
              value={form.rate_limit_per_minute}
              onChange={(e) =>
                setForm({
                  ...form,
                  rate_limit_per_minute: e.target.value,
                })
              }
            />
              </Box>
            </Box>
          </Box>

          {subscribesTelemetry ? (
            <Box sx={sectionSx}>
              {sectionHeading(
                "4. Telemetry settings",
                "These controls apply only to telemetry.received events.",
              )}
              <Box sx={gridSx}>
                <Box sx={{ gridColumn: { xs: "1", md: "span 4" } }}>
                  <FormControl fullWidth>
                    <InputLabel>Telemetry source</InputLabel>
                    <Select
                      label="Telemetry source"
                      value={form.target_scope}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          target_scope: e.target.value,
                          target_device_id: "",
                          target_device_ids: [],
                          target_device_type_id: "",
                          target_device_type_ids: [],
                        })
                      }
                    >
                      <MenuItem value="organization">
                        All devices in organization
                      </MenuItem>
                      <MenuItem value="device">Specific device</MenuItem>
                      <MenuItem value="device_type">Device type</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {form.target_scope === "device" && (
                  <Box sx={{ gridColumn: { xs: "1", md: "span 4" } }}>
                    <Autocomplete
                      multiple
                      options={targetDevices || []}
                      value={selectedTargetDevices}
                      onChange={(_, value) =>
                        setForm({
                          ...form,
                          target_device_id: value?.[0]?.id || "",
                          target_device_ids: value.map((item) => item.id),
                        })
                      }
                      getOptionLabel={(option) => option?.name || ""}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Target devices" required />
                      )}
                    />
                  </Box>
                )}

                {form.target_scope === "device_type" && (
                  <Box sx={{ gridColumn: { xs: "1", md: "span 4" } }}>
                    <Autocomplete
                      multiple
                      options={targetDeviceTypes || []}
                      value={selectedTargetDeviceTypes}
                      onChange={(_, value) =>
                        setForm({
                          ...form,
                          target_device_type_id: value?.[0]?.id || "",
                          target_device_type_ids: value.map((item) => item.id),
                        })
                      }
                      getOptionLabel={(option) => option?.name || ""}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Target device types"
                          required
                        />
                      )}
                    />
                  </Box>
                )}

                <Box sx={{ gridColumn: { xs: "1", md: "span 4" } }}>
                  <FormControl fullWidth>
                    <InputLabel>Telemetry delivery</InputLabel>
                    <Select
                      label="Telemetry delivery"
                      value={form.telemetry_delivery_mode}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          telemetry_delivery_mode: e.target.value,
                        })
                      }
                    >
                      <MenuItem value="real_time">Real-time</MenuItem>
                      <MenuItem value="batch">Batched</MenuItem>
                      <MenuItem value="threshold">Alert / threshold</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {form.telemetry_delivery_mode === "batch" && (
                  <>
                    <Box sx={{ gridColumn: { xs: "1", md: "span 4" } }}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Batch interval (seconds)"
                        value={form.batch_interval_seconds}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            batch_interval_seconds: e.target.value,
                          })
                        }
                      />
                    </Box>
                    <Box sx={{ gridColumn: { xs: "1", md: "span 4" } }}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Max records / batch"
                        value={form.batch_max_records}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            batch_max_records: e.target.value,
                          })
                        }
                      />
                    </Box>
                  </>
                )}

                {form.telemetry_delivery_mode === "threshold" && (
                  <>
                    <Box sx={{ gridColumn: { xs: "1", md: "span 4" } }}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Cooldown (seconds)"
                        value={form.threshold_cooldown_seconds}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            threshold_cooldown_seconds: e.target.value,
                          })
                        }
                      />
                    </Box>
                    <Box sx={{ gridColumn: { xs: "1", md: "span 8" } }}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="Threshold rules (JSON)"
                        placeholder='{"Temperature":{"gt":80},"Battery":{"lt":20}}'
                        value={thresholdJson}
                        onChange={(e) => setThresholdJson(e.target.value)}
                        InputProps={{
                          sx: { fontFamily: "monospace", fontSize: 13 },
                        }}
                      />
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          ) : null}

          <Box sx={{ ...sectionSx, display: "none" }}>
            {sectionHeading(
              "4. Events",
              "Select the event types that should call this endpoint.",
            )}
            <Box sx={gridSx}>
              <Box sx={{ gridColumn: { xs: "1", md: "span 4" } }}>
            <FormControl fullWidth>
              <InputLabel>Event domain filter</InputLabel>

              <Select
                label="Event domain filter"
                value={domainFilter}
                onChange={(e) => setDomainFilter(e.target.value)}
              >
                {domains.map((d) => (
                  <MenuItem key={d} value={d}>
                    {d === "all" ? "All domains" : d}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
              </Box>

              <Box sx={{ gridColumn: { xs: "1", md: "span 8" } }}>
            <Autocomplete
              multiple
              options={filteredCatalog}
              groupBy={(option) => option.domain}
              getOptionLabel={(option) => option.type}
              value={selectedEventObjects}
              onChange={(_, value) =>
                setForm({
                  ...form,
                  subscribed_events: value.map((v) => v.type),
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Subscribed events"
                  required
                  error={eventsError}
                  helperText={
                    eventsError ? "Select at least one event type." : ""
                  }
                  placeholder="Search events…"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.type}
                    size="small"
                    label={option.type}
                  />
                ))
              }
              renderOption={(props, option) => (
                <li {...props} key={option.type}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {option.type}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      {option.description}
                      {option.high_volume ? " · high volume" : ""}
                    </Typography>
                  </Box>
                </li>
              )}
            />
              </Box>
            </Box>
          </Box>

          <Box sx={sectionSx}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              {subscribesTelemetry ? "5. Payload and filters" : "4. Payload and filters"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Optional advanced controls for custom request bodies, filters, and extra headers.
            </Typography>

            <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  setForm({
                    ...form,
                    template_body: DEFAULT_WEBHOOK_TEMPLATE,
                  })
                }
              >
                Default
              </Button>

              {subscribesTelemetry && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      setForm({
                        ...form,
                        template_body:
                          form.telemetry_delivery_mode === "batch"
                            ? TELEMETRY_BATCH_WEBHOOK_TEMPLATE
                            : TELEMETRY_WEBHOOK_TEMPLATE,
                      })
                    }
                  >
                    {form.telemetry_delivery_mode === "batch" ? "All batch records" : "All sensors"}
                  </Button>

                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      setForm({
                        ...form,
                        template_body:
                          form.telemetry_delivery_mode === "batch"
                            ? TELEMETRY_BATCH_CUSTOM_FIELDS_TEMPLATE
                            : TELEMETRY_CUSTOM_FIELDS_TEMPLATE,
                      })
                    }
                  >
                    {form.telemetry_delivery_mode === "batch"
                      ? "Example: pick fields from each record"
                      : "Example: pick fields"}
                  </Button>
                </>
              )}
            </Stack>

            <TextField
              fullWidth
              multiline
              minRows={10}
              label="Edit Jinja template manually"
              value={form.template_body}
              onChange={(e) =>
                setForm({
                  ...form,
                  template_body: e.target.value,
                })
              }
              helperText={
                subscribesTelemetry
                  ? form.telemetry_delivery_mode === "batch"
                    ? "Batched telemetry values: loop over resource.attributes['records']; each record has record['data']['YourField']."
                    : "Telemetry values: resource.attributes['data']['YourField']."
                  : "Customize the JSON body sent to your webhook URL."
              }
              InputProps={{
                sx: {
                  fontFamily: "monospace",
                  fontSize: 13,
                },
              }}
            />

            <TextField
              fullWidth
              multiline
              minRows={10}
              label="Filter rules (JSON, optional)"
              placeholder='{"device_id":["66a00af9-61c0-4a2c-85f7-50f2ebd8883f"]}'
              value={filterJson}
              onChange={(e) => setFilterJson(e.target.value)}
              sx={{ mt: 2 }}
              InputProps={{
                sx: {
                  fontFamily: "monospace",
                  fontSize: 13,
                },
              }}
            />

          {form.auth_type !== "custom_headers" && (
              <TextField
                fullWidth
                multiline
                minRows={5}
                label="Extra template headers (JSON, optional)"
                value={headersJson}
                onChange={(e) => setHeadersJson(e.target.value)}
                sx={{ mt: 2 }}
                InputProps={{
                  sx: {
                    fontFamily: "monospace",
                    fontSize: 13,
                  },
                }}
              />
          )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ background: "linear-gradient(135deg, #FDE68A, #F59E0B)" }}
        >
          {saving ? "Saving…" : isEdit ? "Save changes" : "Create endpoint"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
